import type OpenAI from 'openai'

import { AI_CHATBOT_CONFIG } from '../config'
import type { Logger } from '../logger'
import { estimateMessagesTokens } from '../token-estimator'
import type { TurnCost } from '../types'

const COMPACTION_PROMPT = `Your task is to create a detailed summary of the CRM assistant conversation so far, so the assistant can continue without losing context.

CRITICAL: Respond with TEXT ONLY. Do NOT call any tools. Your entire response must be plain text.

Your summary must include ALL of the following sections:

1. Primary Request and Intent: What did the user explicitly ask for? List every request.
2. Key Technical Concepts: Entity types queried, field names used, filters applied, IDs referenced.
3. Data Retrieved: Specific records, counts, statuses, and field values surfaced during the conversation. Include entity names and IDs. Preserve every entity ID, dataClass, and confirmation status exactly as stated — never paraphrase or omit them.
4. Errors and Fixes: Any errors encountered and how they were resolved.
5. User Decisions: Choices the user made (e.g. selected options, confirmed actions, navigation targets).
6. All User Messages: List every user message verbatim (not tool results).
7. Pending Tasks: Anything the user explicitly asked to do that has not yet been completed.
8. Current Work: What was being worked on immediately before this summary. Be specific — include entity names, IDs, field values.
9. Next Step: The most likely next action given the conversation state.`

export interface CompactionResult {
  messages: OpenAI.ChatCompletionMessageParam[]
  compacted: boolean
  cost?: TurnCost
}

export interface CompactionClient {
  chat: {
    completions: {
      stream: (params: {
        model: string
        messages: OpenAI.ChatCompletionMessageParam[]
        stream: true
        stream_options?: { include_usage: boolean }
      }) => {
        finalChatCompletion: () => Promise<OpenAI.Chat.Completions.ChatCompletion>
      }
    }
  }
}

// Re-export so callers can import from one place (compaction.ts) without
// knowing about token-estimator.ts directly.
export { estimateMessagesTokens }

export async function compactHistory(
  client: CompactionClient,
  messages: OpenAI.ChatCompletionMessageParam[],
  log: Logger,
  model: string = AI_CHATBOT_CONFIG.models.compact,
): Promise<CompactionResult> {
  const { preserveRecentTurns, postCompactBudgetTokens } =
    AI_CHATBOT_CONFIG.contextWindow

  if (messages.length <= preserveRecentTurns * 2) {
    return { messages, compacted: false }
  }

  // Separate pinned messages from conversational history. Pinned messages survive
  // compaction verbatim — they carry formatting rules, behaviour constraints, and
  // per-turn context the model must always see. Only user/assistant turns go into
  // the summarizer.
  // page_context is emitted as a 'user' message (not 'system') so it does not
  // invalidate the Bedrock cached prefix on navigation; identify it by content marker.
  const isPageContext = (m: OpenAI.ChatCompletionMessageParam) =>
    typeof m.content === 'string' && m.content.startsWith('<page_context')
  const pinnedMessages = messages.filter(
    (m) => m.role === 'system' || isPageContext(m),
  )
  const conversational = messages.filter(
    (m) => m.role !== 'system' && !isPageContext(m),
  )

  if (conversational.length <= preserveRecentTurns * 2) {
    return { messages, compacted: false }
  }

  const stable = conversational.slice(0, -preserveRecentTurns * 2)
  const recent = conversational.slice(-preserveRecentTurns * 2)

  const historyText = stable
    .map((m) => {
      const role = m.role === 'assistant' ? 'Assistant' : 'User'
      const content =
        typeof m.content === 'string'
          ? m.content
          : Array.isArray(m.content)
            ? m.content.map((p) => (p.type === 'text' ? p.text : '')).join('')
            : ''
      return `${role}: ${content}`
    })
    .join('\n')

  const startMs = Date.now()
  const completion = await client.chat.completions
    .stream({
      model,
      messages: [
        { role: 'system', content: COMPACTION_PROMPT },
        { role: 'user', content: historyText },
      ],
      stream: true,
      stream_options: { include_usage: true },
    })
    .finalChatCompletion()

  const durationMs = Date.now() - startMs
  const summary = completion.choices[0]?.message.content ?? ''

  if (!summary) {
    throw new Error('Compaction returned empty summary')
  }

  const summaryMessage: OpenAI.ChatCompletionMessageParam = {
    role: 'system',
    content: `[Conversation summary: ${summary}]`,
  }

  // Pinned system messages go first so the model always sees them at the top,
  // followed by the compaction summary, then the preserved recent turns.
  const compacted = [...pinnedMessages, summaryMessage, ...recent]
  const compactedTokens = estimateMessagesTokens(compacted)

  if (compactedTokens > postCompactBudgetTokens) {
    throw new Error(
      `Post-compaction size (${compactedTokens} tokens) exceeds budget (${postCompactBudgetTokens})`,
    )
  }

  const usage = completion.usage
  const cost: TurnCost | undefined = usage
    ? {
        stage: 'compact',
        model,
        inputCredits: usage.prompt_tokens ?? 0,
        outputCredits: usage.completion_tokens ?? 0,
        cacheReadInputCredits: 0,
        cacheCreationInputCredits: 0,
        durationMs,
      }
    : undefined

  log.log(
    'Compaction complete. Model:',
    model,
    '| Stable turns:',
    stable.length,
    '→ 1 summary. Pinned messages kept (system + user page_context):',
    pinnedMessages.length,
    '. Recent kept:',
    recent.length,
    '. Estimated tokens:',
    compactedTokens,
  )

  return { messages: compacted, compacted: true, cost }
}
